import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import SimpleBar from 'simplebar';

// Cria um Contexto para o layout
const LayoutContext = createContext();

// Hook personalizado para usar as configurações de layout
export const useLayout = () => useContext(LayoutContext);

// Lista de configurações e seus valores padrão
const settings = [
  { attribute: 'data-layout', defaultValue: 'vertical' },
  { attribute: 'data-bs-theme', defaultValue: 'light' },
  { attribute: 'data-content-width', defaultValue: 'default' },
  { attribute: 'dir', defaultValue: 'ltr' },
  { attribute: 'data-sidebar-color', defaultValue: 'light' },
  { attribute: 'data-sidebar', defaultValue: 'default' },
  { attribute: 'data-theme-colors', defaultValue: 'default' },
];

export const LayoutProvider = ({ children }) => {
  const [layoutState, setLayoutState] = useState(() => {
    // Inicializa o estado com base no localStorage
    const initialState = {};
    settings.forEach(({ attribute, defaultValue }) => {
      initialState[attribute] = localStorage.getItem(attribute) || defaultValue;
    });
    return initialState;
  });

  // Salva o estado no localStorage e atualiza o <html>
  const setAndSaveAttribute = useCallback((attribute, value) => {
    setLayoutState(prevState => ({ ...prevState, [attribute]: value }));
    localStorage.setItem(attribute, value);
  }, []);

  // Efeito para sincronizar o estado do React com os atributos do <html>
  useEffect(() => {
    const htmlElement = document.documentElement;
    Object.entries(layoutState).forEach(([attribute, value]) => {
      htmlElement.setAttribute(attribute, value);
    });

    // Lógica para layout horizontal (remover atributos)
    if (layoutState['data-layout'] === 'horizontal') {
      htmlElement.removeAttribute('data-sidebar');
      htmlElement.setAttribute('data-topbar-theme', 'dark');
    } else {
      htmlElement.removeAttribute('data-topbar-theme');
    }
    
    // Lógica para atualização de SimpleBar
    const sidebarSimpleBar = document.getElementById('sidebar-simplebar');
    if (sidebarSimpleBar && window.SimpleBar) {
      const isSimpleBarEnabled = layoutState['data-layout'] !== 'icon' && layoutState['data-sidebar'] !== 'icon';
      const simpleBarInstance = SimpleBar.instances.get(sidebarSimpleBar);

      if (isSimpleBarEnabled && !simpleBarInstance) {
        new SimpleBar(sidebarSimpleBar);
      } else if (!isSimpleBarEnabled && simpleBarInstance) {
        simpleBarInstance.unMount();
      }
    }
  }, [layoutState]);

  // Efeito para lidar com a responsividade
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992) {
        document.documentElement.removeAttribute('data-sidebar');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Efeito para controlar o modo claro/escuro automático
  useEffect(() => {
    if (layoutState['data-bs-theme'] === 'auto') {
      const theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      document.documentElement.setAttribute('data-bs-theme', theme);
    }
  }, [layoutState['data-bs-theme']]);


  const resetSettings = useCallback(() => {
    localStorage.clear();
    window.location.reload();
  }, []);

  // O valor do contexto que será fornecido aos filhos
  const contextValue = {
    layoutState,
    setAndSaveAttribute,
    resetSettings,
    toggleSidebar: () => {
      const currentSidebar = layoutState['data-sidebar'];
      const newSidebar = currentSidebar === 'icon' ? 'default' : 'icon';
      setAndSaveAttribute('data-sidebar', newSidebar);
    },
    toggleTheme: () => {
      const currentTheme = layoutState['data-bs-theme'];
      const newTheme = currentTheme === 'light' ? 'dark' : 'light';
      setAndSaveAttribute('data-bs-theme', newTheme);
    },
  };

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};